import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import Splash from "@/components/Splash";
import PWAPrompt from "@/components/PWAPrompt";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import Home from "@/pages/marketing/Home";
import Products from "@/pages/marketing/Products";
import Pricing from "@/pages/marketing/Pricing";
import Blog from "@/pages/marketing/Blog";
import BlogPost from "@/pages/marketing/BlogPost";
import AboutUs from "@/pages/marketing/AboutUs";
import ContactUs from "@/pages/marketing/ContactUs";
import Legal from "@/pages/marketing/Legal";
import Login from "@/pages/Login";

import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import CalendarView from "@/pages/dashboard/CalendarView";
import BookingsView from "@/pages/dashboard/BookingsView";
import AnalyticsView from "@/pages/dashboard/AnalyticsView";
import EmployeesView from "@/pages/dashboard/EmployeesView";
import SettingsView from "@/pages/dashboard/SettingsView";
import AdminPanel from "@/pages/admin/AdminPanel";
import ProductsHub from "@/pages/dashboard/ProductsHub";
import ProfitLossView from "@/pages/dashboard/ProfitLossView";
import PaymentsView from "@/pages/dashboard/PaymentsView";
import PendingPaymentsView from "@/pages/dashboard/PendingPaymentsView";
import { useAuth } from "@/context/AuthContext";

const DashHome = () => {
  const { user } = useAuth();
  if (user?.role === "employee") return <Navigate to="/dashboard/calendar" replace />;
  return <ProductsHub />;
};

class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("App crash:", error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="grid min-h-screen place-items-center bg-white p-8">
          <div className="max-w-lg text-center">
            <div className="text-2xl font-bold text-pulse-navy">Something went wrong</div>
            <pre className="mt-4 overflow-auto rounded-md bg-cloud p-4 text-left text-xs text-red-600">{String(this.state.error?.stack || this.state.error)}</pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const MarketingLayout = () => (
  <div className="flex min-h-screen flex-col bg-white">
    <Navbar />
    <main className="flex-1"><Outlet /></main>
    <Footer />
  </div>
);

let splashSkip = false;
try {
  splashSkip = !!sessionStorage.getItem("pulse_splashed") || window.location.search.includes("nosplash");
} catch (e) {}

function AppShell() {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      {!splashSkip && <Splash />}
      <ErrorBoundary>
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
          </Route>
          <Route path="/login" element={<Login />} />

          <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<DashHome />} />
            <Route path="calendar" element={<CalendarView />} />
            <Route path="bookings" element={<BookingsView />} />
            <Route path="analytics" element={<AnalyticsView />} />
            <Route path="profit-loss" element={<ProfitLossView />} />
            <Route path="payments" element={<PaymentsView />} />
            <Route path="pending" element={<PendingPaymentsView />} />
            <Route path="employees" element={<EmployeesView />} />
            <Route path="settings" element={<SettingsView />} />
          </Route>

          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />
        </Routes>
      </ErrorBoundary>
      <PWAPrompt />
      <Toaster position="top-center" richColors />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AuthProvider>
  );
}
